import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Gift, Sparkles, X } from 'lucide-react';
import { Button } from '../ui/button';
import { appConfig } from '../../config/featureFlags';

// ═══════════════════════════════════════════════════════════════
// SLOT MACHINE - Premium 3D Vegas-Style
// Logika: 1/100 szans na wygraną (bez zmian)
// ═══════════════════════════════════════════════════════════════

export const SlotMachine = ({ onClose, onWin }) => {
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(['🎯', '🐱', '🎯']);
  const [hasWon, setHasWon] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [leverPulled, setLeverPulled] = useState(false);
  const [reelOffsets, setReelOffsets] = useState([0, 0, 0]);
  
  const symbols = ['🎯', '🐱'];
  
  const spin = () => {
    setSpinning(true);
    setShowResult(false);
    setHasWon(null);
    setLeverPulled(true);
    
    // Reset lever after animation
    setTimeout(() => setLeverPulled(false), 600);
    
    // Animate reels with different speeds
    let spinCount = 0;
    const maxSpins = 25;
    
    const interval = setInterval(() => {
      // Update reel offsets for 3D rotation effect
      setReelOffsets(prev => [
        (prev[0] + 45) % 360,
        (prev[1] + 50) % 360,
        (prev[2] + 55) % 360
      ]);
      
      setResult([
        symbols[Math.floor(Math.random() * symbols.length)],
        symbols[Math.floor(Math.random() * symbols.length)],
        symbols[Math.floor(Math.random() * symbols.length)]
      ]);
      spinCount++;
      
      if (spinCount >= maxSpins) {
        clearInterval(interval);
        
        // Reset offsets with bounce
        setReelOffsets([0, 0, 0]);
        
        // Determine result (1/100 chance - unchanged logic)
        const won = Math.random() < appConfig.slotMachineWinChance;
        
        if (won) {
          setResult(['🎯', '🎯', '🎯']);
          setHasWon(true);
          setTimeout(() => {
            setShowResult(true);
            onWin?.();
          }, 800);
        } else {
          let finalResult;
          do {
            finalResult = [
              symbols[Math.floor(Math.random() * symbols.length)],
              symbols[Math.floor(Math.random() * symbols.length)],
              symbols[Math.floor(Math.random() * symbols.length)]
            ];
          } while (finalResult[0] === finalResult[1] && finalResult[1] === finalResult[2]);
          
          setResult(finalResult);
          setHasWon(false);
          setTimeout(() => setShowResult(true), 800);
        }
        
        setSpinning(false);
      }
    }, 80);
  };

  const softMessages = [
    "Tym razem się nie udało – ale świetnie ukończyłeś Dzień 1!",
    "Prawie! Kot nie chciał współpracować 🐱",
    "Blisko! Następnym razem będzie lepiej.",
    "Losowanie nie poszło po Twojej myśli, ale Twoje umiejętności już tak!"
  ];

  const getRandomMessage = () => softMessages[Math.floor(Math.random() * softMessages.length)];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Premium dark background with gradient */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #0d0d15 50%, #000000 100%)'
        }}
      />
      
      {/* Ambient glow behind machine */}
      <div 
        className="absolute w-[500px] h-[500px] rounded-full opacity-30"
        style={{
          background: 'radial-gradient(circle, rgba(255,193,7,0.3) 0%, transparent 70%)',
          filter: 'blur(60px)'
        }}
      />
      
      {/* Main container */}
      <div className="relative w-full max-w-sm animate-scaleIn">
        
        {/* Header */}
        <div className="text-center mb-6 relative z-10">
          <div 
            className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{
              background: 'linear-gradient(145deg, #ffd700, #b8860b)',
              boxShadow: '0 0 40px rgba(255,215,0,0.4), inset 0 2px 10px rgba(255,255,255,0.3)'
            }}
          >
            <Gift className="w-10 h-10 text-white drop-shadow-lg" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-1" style={{ textShadow: '0 0 20px rgba(255,215,0,0.5)' }}>
            Darmowe losowanie
          </h2>
          <p className="text-gray-400 text-sm">Nagroda za ukończenie Dnia 1</p>
        </div>

        {/* 3D Slot Machine Body */}
        <div 
          className="relative rounded-3xl p-1 mx-auto"
          style={{
            background: 'linear-gradient(145deg, #ffd700, #daa520, #b8860b, #8b6914)',
            boxShadow: `
              0 20px 60px rgba(0,0,0,0.8),
              0 0 80px rgba(255,215,0,0.15),
              inset 0 2px 4px rgba(255,255,255,0.4),
              inset 0 -2px 4px rgba(0,0,0,0.3)
            `,
            transform: 'perspective(800px) rotateX(2deg)'
          }}
        >
          {/* Inner chrome bezel */}
          <div 
            className="rounded-[22px] p-1"
            style={{
              background: 'linear-gradient(180deg, #e8e8e8, #a0a0a0, #c0c0c0)',
              boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.3)'
            }}
          >
            {/* Dark interior */}
            <div 
              className="rounded-[18px] p-6"
              style={{
                background: 'linear-gradient(180deg, #1a1a2e, #0f0f1a)',
                boxShadow: 'inset 0 4px 20px rgba(0,0,0,0.8)'
              }}
            >
              {/* LED strip top */}
              <div 
                className="h-1 rounded-full mb-4 mx-auto w-3/4"
                style={{
                  background: hasWon && showResult 
                    ? 'linear-gradient(90deg, #ffd700, #ffed4a, #ffd700)'
                    : 'linear-gradient(90deg, rgba(255,215,0,0.3), rgba(255,215,0,0.6), rgba(255,215,0,0.3))',
                  boxShadow: hasWon && showResult 
                    ? '0 0 20px rgba(255,215,0,0.8)'
                    : '0 0 10px rgba(255,215,0,0.3)'
                }}
              />
              
              {/* Reels container */}
              <div 
                className="flex justify-center gap-3 mb-4 p-4 rounded-2xl"
                style={{
                  background: 'linear-gradient(180deg, #0a0a12, #151520)',
                  boxShadow: 'inset 0 4px 15px rgba(0,0,0,0.9), 0 2px 4px rgba(255,215,0,0.1)'
                }}
              >
                {result.map((symbol, idx) => (
                  <div
                    key={idx}
                    className="relative"
                    style={{
                      perspective: '200px'
                    }}
                  >
                    {/* Reel */}
                    <div
                      className="w-[70px] h-[80px] rounded-xl flex items-center justify-center text-5xl relative overflow-hidden"
                      style={{
                        background: 'linear-gradient(180deg, #ffffff, #f0f0f0, #e0e0e0, #f5f5f5)',
                        boxShadow: `
                          inset 0 3px 8px rgba(0,0,0,0.2),
                          inset 0 -3px 8px rgba(0,0,0,0.1),
                          0 4px 15px rgba(0,0,0,0.5)
                        `,
                        transform: spinning 
                          ? `rotateX(${reelOffsets[idx]}deg)` 
                          : 'rotateX(0deg)',
                        transition: spinning ? 'none' : 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transformStyle: 'preserve-3d'
                      }}
                    >
                      {/* Symbol with motion blur effect during spin */}
                      <span 
                        style={{ 
                          filter: spinning ? 'blur(2px)' : 'none',
                          transition: 'filter 0.2s ease'
                        }}
                      >
                        {symbol}
                      </span>
                      
                      {/* Gloss overlay */}
                      <div 
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.1) 100%)',
                          borderRadius: 'inherit'
                        }}
                      />
                      
                      {/* Win highlight */}
                      {hasWon && showResult && (
                        <div 
                          className="absolute inset-0 rounded-xl animate-pulse"
                          style={{
                            boxShadow: '0 0 30px rgba(255,215,0,0.8), inset 0 0 20px rgba(255,215,0,0.3)',
                            border: '2px solid rgba(255,215,0,0.6)'
                          }}
                        />
                      )}
                    </div>
                    
                    {/* Reel shadow */}
                    <div 
                      className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-14 h-3 rounded-full"
                      style={{
                        background: 'radial-gradient(ellipse, rgba(0,0,0,0.5) 0%, transparent 70%)'
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* LED strip bottom */}
              <div 
                className="h-1 rounded-full mx-auto w-3/4"
                style={{
                  background: hasWon && showResult 
                    ? 'linear-gradient(90deg, #ffd700, #ffed4a, #ffd700)'
                    : 'linear-gradient(90deg, rgba(255,215,0,0.3), rgba(255,215,0,0.6), rgba(255,215,0,0.3))',
                  boxShadow: hasWon && showResult 
                    ? '0 0 20px rgba(255,215,0,0.8)'
                    : '0 0 10px rgba(255,215,0,0.3)'
                }}
              />
            </div>
          </div>
          
          {/* Lever (right side) */}
          <div 
            className="absolute -right-6 top-1/2 -translate-y-1/2"
            style={{
              transform: `translateY(-50%) ${leverPulled ? 'rotate(25deg)' : 'rotate(0deg)'}`,
              transformOrigin: 'top center',
              transition: leverPulled 
                ? 'transform 0.15s ease-in' 
                : 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
            }}
          >
            {/* Lever arm */}
            <div 
              className="w-3 h-20 rounded-full"
              style={{
                background: 'linear-gradient(90deg, #c0c0c0, #e8e8e8, #a0a0a0)',
                boxShadow: '2px 0 8px rgba(0,0,0,0.4), inset 1px 0 2px rgba(255,255,255,0.5)'
              }}
            />
            {/* Gold ball */}
            <div 
              className="w-8 h-8 rounded-full -ml-2.5 mt-1"
              style={{
                background: 'radial-gradient(circle at 30% 30%, #ffed4a, #ffd700, #b8860b)',
                boxShadow: '0 4px 15px rgba(0,0,0,0.5), inset 0 2px 4px rgba(255,255,255,0.6)'
              }}
            />
          </div>
        </div>

        {/* Legend */}
        <div className="text-center mt-4 mb-4">
          <p className="text-gray-500 text-xs">
            🎯 PromptOrigin &nbsp;•&nbsp; 🐱 Czarny Kot
          </p>
        </div>

        {/* Result Message */}
        {showResult && (
          <div 
            className="mb-4 p-5 rounded-2xl text-center animate-slideUp mx-4"
            style={{
              background: hasWon 
                ? 'linear-gradient(145deg, rgba(255,215,0,0.2), rgba(184,134,11,0.2))'
                : 'rgba(255,255,255,0.05)',
              border: hasWon ? '1px solid rgba(255,215,0,0.4)' : '1px solid rgba(255,255,255,0.1)',
              boxShadow: hasWon ? '0 0 40px rgba(255,215,0,0.2)' : 'none'
            }}
          >
            {hasWon ? (
              <>
                <div className="flex justify-center mb-3">
                  <Sparkles className="w-10 h-10 text-yellow-400 animate-pulse" />
                </div>
                <p className="font-bold text-xl text-yellow-400 mb-1">WYGRAŁEŚ!</p>
                <p className="text-sm text-gray-300">24h darmowego dostępu PRO!</p>
              </>
            ) : (
              <p className="text-sm text-gray-400 leading-relaxed">{getRandomMessage()}</p>
            )}
          </div>
        )}

        {/* Buttons */}
        <div className="px-4 space-y-3">
          {!showResult ? (
            <button
              onClick={spin}
              disabled={spinning}
              className="w-full h-14 rounded-2xl font-bold text-lg relative overflow-hidden transition-all duration-300"
              style={{
                background: spinning 
                  ? 'linear-gradient(145deg, #8b6914, #6b5310)'
                  : 'linear-gradient(145deg, #ffd700, #daa520, #b8860b)',
                boxShadow: spinning
                  ? 'inset 0 2px 10px rgba(0,0,0,0.4)'
                  : '0 4px 20px rgba(255,215,0,0.4), inset 0 2px 4px rgba(255,255,255,0.3)',
                color: spinning ? '#a0a0a0' : '#1a1a2e',
                transform: spinning ? 'scale(0.98)' : 'scale(1)'
              }}
            >
              {spinning ? (
                <span className="flex items-center justify-center gap-3">
                  <div className="w-5 h-5 border-3 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  Losuję...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  🎰 Zakręć!
                </span>
              )}
            </button>
          ) : (
            <button
              onClick={onClose}
              className="w-full h-14 rounded-2xl font-bold text-lg text-white transition-all duration-300"
              style={{
                background: hasWon 
                  ? 'linear-gradient(145deg, #ffd700, #b8860b)'
                  : 'linear-gradient(145deg, #0ea5e9, #0284c7)',
                boxShadow: hasWon
                  ? '0 4px 20px rgba(255,215,0,0.4)'
                  : '0 4px 20px rgba(14,165,233,0.4)'
              }}
            >
              {hasWon ? '🎁 Odbierz nagrodę' : 'Kontynuuj naukę →'}
            </button>
          )}
        </div>

        {/* Close button (only after result) */}
        {showResult && (
          <button
            onClick={onClose}
            className="absolute top-0 right-0 p-2 text-gray-500 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        )}
        
        {/* Win particles effect */}
        {hasWon && showResult && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 rounded-full animate-ping"
                style={{
                  background: 'linear-gradient(145deg, #ffd700, #ffed4a)',
                  left: `${10 + (i * 8)}%`,
                  top: `${20 + (i % 3) * 20}%`,
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: '1.5s',
                  boxShadow: '0 0 10px rgba(255,215,0,0.8)'
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SlotMachine;
